import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

function AcceptanceTimeoutsConfig() {
    const BASE_URL = process.env.REACT_APP_BASEURL;
    const getAuthToken = () => localStorage.getItem("astroVani-admin-token");

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            dayAcceptanceTimeout: 0,
            nightAcceptanceTimeout: 0
        }
    });

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch(`${BASE_URL}/admin/acceptanceTimeouts`, {
                    headers: {
                        'Authorization': getAuthToken(),
                    },
                });

                if (!response.ok) throw new Error("Failed to fetch acceptance timeouts");
                const data = await response.json();

                if (data?.data) {
                    reset({
                        dayAcceptanceTimeout: data.data.dayAcceptanceTimeout ?? 0,
                        nightAcceptanceTimeout: data.data.nightAcceptanceTimeout ?? 0
                    });
                }
            } catch (error) {
                console.error("Fetch error:", error);
                toast.error(error.message || "Failed to fetch details");
            }
        };

        fetchConfig();
    }, [BASE_URL, reset]);

    const onSubmit = async (data) => {
        try {
            const dayStr = String(data.dayAcceptanceTimeout);
            const nightStr = String(data.nightAcceptanceTimeout);
            if (dayStr.includes('.') || nightStr.includes('.') || !/^\d+$/.test(dayStr) || !/^\d+$/.test(nightStr)) {
                toast.error("Only integer values are allowed (no decimals).");
                return;
            }

            const payload = {
                dayAcceptanceTimeout: parseInt(data.dayAcceptanceTimeout, 10),
                nightAcceptanceTimeout: parseInt(data.nightAcceptanceTimeout, 10)
            };

            const response = await fetch(`${BASE_URL}/admin/acceptanceTimeouts`, {
                method: 'PATCH',
                headers: {
                    'Authorization': getAuthToken(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (!response.ok || (result && result.success === false) || (result && result.status === 'error')) {
                throw new Error(result.message || "Failed to update config");
            }

            toast.success(result.message || "Config updated successfully");
        } catch (error) {
            console.error("Update error:", error);
            toast.error(error.message || "Failed to update config");
        }
    };

    return (
        <div className="card mt-3">
            <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row g-3 align-items-center">
                        <div className="col-md-6">
                            <label className="form-label">Day Acceptance Timeout</label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    step="1"
                                    onKeyDown={(e) => {
                                        if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    onPaste={(e) => {
                                        const pasteData = e.clipboardData.getData('text');
                                        if (pasteData.includes('.') || !/^\d+$/.test(pasteData.trim())) {
                                            e.preventDefault();
                                            toast.error("Only whole integer numbers are allowed");
                                        }
                                    }}
                                    className={`form-control ${errors.dayAcceptanceTimeout ? 'is-invalid' : ''}`}
                                    placeholder="Enter timeout in hours"
                                    {...register('dayAcceptanceTimeout', {
                                        required: "Day timeout is required",
                                        min: { value: 1, message: "Minimum is 1" },
                                        validate: (val) => {
                                            if (val === '' || val === null || val === undefined) return true;
                                            if (String(val).includes('.') || !/^\d+$/.test(String(val))) {
                                                return "Only integer values are allowed (no decimals)";
                                            }
                                            return true;
                                        }
                                    })}
                                />
                                <span className="input-group-text">hours</span>
                                {errors.dayAcceptanceTimeout && (
                                    <div className="invalid-feedback">
                                        {errors.dayAcceptanceTimeout.message}
                                    </div>
                                )}
                            </div>
                            <small className="text-muted mt-2 d-block">
                                Timeout for accepting requests during the day
                            </small>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Night Acceptance Timeout</label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    step="1"
                                    onKeyDown={(e) => {
                                        if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    onPaste={(e) => {
                                        const pasteData = e.clipboardData.getData('text');
                                        if (pasteData.includes('.') || !/^\d+$/.test(pasteData.trim())) {
                                            e.preventDefault();
                                            toast.error("Only whole integer numbers are allowed");
                                        }
                                    }}
                                    className={`form-control ${errors.nightAcceptanceTimeout ? 'is-invalid' : ''}`}
                                    placeholder="Enter timeout in hours"
                                    {...register('nightAcceptanceTimeout', {
                                        required: "Night timeout is required",
                                        min: { value: 1, message: "Minimum is 1" },
                                        validate: (val) => {
                                            if (val === '' || val === null || val === undefined) return true;
                                            if (String(val).includes('.') || !/^\d+$/.test(String(val))) {
                                                return "Only integer values are allowed (no decimals)";
                                            }
                                            return true;
                                        }
                                    })}
                                />
                                <span className="input-group-text">hours</span>
                                {errors.nightAcceptanceTimeout && (
                                    <div className="invalid-feedback">
                                        {errors.nightAcceptanceTimeout.message}
                                    </div>
                                )}
                            </div>
                            <small className="text-muted mt-2 d-block">
                                Timeout for accepting requests during the night
                            </small>
                        </div>
                        <div className="col-12 mt-4 text-end">
                            <button type="submit" className="btn btn-primary">
                                Save Changes
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AcceptanceTimeoutsConfig;
